import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { LayoutSplashScreen } from "../../../../_metronic/layout/core";
import { AuthModel, UserModel } from "./_models";
import * as authHelper from "./AuthHelpers";
import { getUserByToken, getUserPermission } from "./_requests";
import { WithChildren } from "../../../../_metronic/helpers";
import { useNavigate } from "react-router-dom";

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  logout: () => void;
  savePermissions: Dispatch<SetStateAction<any | undefined>>;
  permissions: any;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  permissions: authHelper.getPermissions(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  savePermissions: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [permissions, setpermissions] = useState(authHelper.getPermissions());


  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };
  
  const savePermissions = (permissions: any) => {
    setpermissions(permissions);
    if (permissions) {
      authHelper.setPermissions(permissions);
    } else {
      authHelper.removePermissions();
    }
  };


  const logout = () => {
    saveAuth(undefined);
    setCurrentUser(undefined);
    authHelper.removePermissions();
    authHelper.removeUser();
    sessionStorage.clear();
    localStorage.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        savePermissions,
        permissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser, currentUser } = useAuth();
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: any) => {
      try {
        if (!currentUser) {
          const {
            data: { data },
          } = await getUserByToken(apiToken);
          
          if (data) {
            setCurrentUser(data);
            authHelper.setUser(data);
          }
        }
      } catch (error) {
        if (!didRequest.current) {
          logout();
        }
      } finally {
        setShowSplashScreen(false);
      }

      return () => (didRequest.current = true);
    };

    if (auth && auth.token) {
      requestUser(auth);
    } else {
      logout();
      setShowSplashScreen(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (showSplashScreen) return;
    if (!auth) return;
    
    const interval = setInterval(() => {
      const authKey = localStorage.getItem("auth-key");
      
      if (!authKey) {
        logout();
        window.location.href = "/auth";
      }
    }, 800);
    
    return () => clearInterval(interval);
  }, [showSplashScreen, auth]);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
